import React, { useState, useEffect } from "react";
import { Button, Box } from "@mui/material";
import 'quill/dist/quill.snow.css';
import QuillEditor from "../../parts/QuillEditor";

interface DocumentEmailContentsProps {
  initialSubject?: string;
  initialBody?: string;
  onSave?: (subject: string, body: string) => void;
}

const DocumentEmailContents = ({ initialSubject = "", initialBody = "", onSave }: DocumentEmailContentsProps) => {
  const [emailSubject, setEmailSubject] = useState(initialSubject);
  const [isEditing, setIsEditing] = useState(false);
  const [htmlContent, setHtmlContent] = useState(initialBody || "");

  useEffect(() => {
    setEmailSubject(initialSubject);
    setHtmlContent(initialBody);
  }, [initialSubject, initialBody]);

  // Helper function to sanitize HTML content for saving
  const sanitizeHtml = (html: string): string => {
    // More careful sanitization that preserves spacing and structure
    return html
      // Preserve paragraph breaks - ensure they have proper spacing
      .replace(/<\/p><p>/g, '</p>\n<p>');
  };

  const handleSave = () => {
    // Sanitize the HTML content before saving
    const finalContent = sanitizeHtml(htmlContent);

    // Save content
    if (onSave) {
      onSave(emailSubject, finalContent);
    }

    // Also update local state with sanitized content
    setHtmlContent(finalContent);

    // Update UI state
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Revert to original content
    setEmailSubject(initialSubject);
    setHtmlContent(initialBody);

    // Update UI state
    setIsEditing(false);
  };

  return (
    <Box>
      {isEditing ? (
        <>
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Email Subject"
            style={{ width: "100%", marginBottom: "16px", padding: "8px", fontSize: "16px" }}
          />
          <Box sx={{ marginBottom: "16px" }}>
            <QuillEditor
              content={htmlContent}
              onChange={setHtmlContent}
              height="400px"
            />
          </Box>
          <Button variant="contained" color="primary" onClick={handleSave} sx={{ marginRight: 2 }}>
            Save
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Box style={{ marginBottom: 16 }}>
            <strong>Email Subject:</strong> {emailSubject || "(No subject)"}
          </Box>
          <Box style={{ marginBottom: 16 }}>
            <strong>Email Body:</strong>
            <div
              className="email-content-display"
              dangerouslySetInnerHTML={{ __html: htmlContent || "(No body)" }}
              style={{
                fontFamily: 'inherit',
              }}
            />
          </Box>
          <Button variant="outlined" color="primary" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        </>
      )}
    </Box>
  );
};

export default DocumentEmailContents;
